import { useEffect, useState, useMemo, FC } from "react"
import { Card, Container, Row, Col, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import API from "../common/API"
import { parsedUser } from "../common/GetCurrentUser"
import { FormatMoney } from "../common/helpers"

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)

interface SellerSalesData {
    vendedorId: number | null
    vendedorNombre: string
    year: number
    month: number
    amount: number
}

interface ProcessedData {
    labels: string[]
    datasets: {
        label: string
        data: number[]
        backgroundColor: string
        borderColor: string
        borderWidth: number
        borderRadius: number
    }[]
}

const SalesBySellerPage: FC = () => {
    const { t } = useTranslation("global")
    const user = parsedUser()
    const [loading, setLoading] = useState<boolean>(true)
    const [salesData, setSalesData] = useState<SellerSalesData[]>([])
    const [error, setError] = useState<string | null>(null)
    const [selectedSeller, setSelectedSeller] = useState<string | null>(null)

    useEffect(() => {
        const fetchSalesData = async () => {
            if (!user?.OrganizationId) {
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                const response = await API.getAction(
                    `dashboard/SalesBySellerGeneral?OrganizationId=${user.OrganizationId}`
                )

                console.log("Seller Sales API Response:", response)

                if (response?.data?.response && Array.isArray(response.data.response)) {
                    console.log("Seller Sales Data:", response.data.response)
                    setSalesData(response.data.response)
                    // Set first seller as default
                    const sellers = Array.from(
                        new Set(response.data.response.map((d: SellerSalesData) => d.vendedorNombre))
                    )
                    if (sellers.length > 0) {
                        setSelectedSeller(sellers[0] as string)
                    }
                } else {
                    console.warn("No valid response data:", response?.data)
                    setSalesData([])
                }
                setError(null)
            } catch (err) {
                console.error("Error fetching seller sales data:", err)
                setError((err as Error)?.message || "Failed to load seller sales data")
                setSalesData([])
            } finally {
                setLoading(false)
            }
        }

        fetchSalesData()
    }, [user?.OrganizationId])

    // Get unique sellers
    const sellers = useMemo(() => {
        return Array.from(
            new Set(
                salesData
                    .filter(
                        (d) =>
                            d.vendedorNombre != null &&
                            d.month >= 1 &&
                            d.month <= 12 &&
                            d.amount != null
                    )
                    .map((d) => d.vendedorNombre)
            )
        ).sort()
    }, [salesData])

    // Process data for selected seller
    const chartData: ProcessedData | null = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return null

        // Filter data for selected seller
        const sellerData = salesData.filter(
            (d) =>
                d.vendedorNombre === selectedSeller &&
                d.year != null &&
                d.month != null &&
                d.month >= 1 &&
                d.month <= 12 &&
                d.amount != null
        )

        if (sellerData.length === 0) return null

        // Get unique years and sort them
        const years = Array.from(new Set(sellerData.map((d) => d.year))).sort()

        // Month labels
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ]

        // Create a map for each year
        const yearDataMap = new Map<number, Map<number, number>>()

        years.forEach((year) => {
            yearDataMap.set(year, new Map())
        })

        // Fill the map with sales data
        sellerData.forEach((item) => {
            const yearMap = yearDataMap.get(item.year)
            if (yearMap) {
                yearMap.set(item.month, item.amount || 0)
            }
        })

        // Find all months that have data
        const allMonths = Array.from(
            new Set(sellerData.map((d) => d.month))
        ).sort((a, b) => a - b)

        // Colors for each year
        const colors = [
            { bg: "rgba(59, 130, 246, 0.7)", border: "rgba(59, 130, 246, 1)" },
            { bg: "rgba(16, 185, 129, 0.7)", border: "rgba(16, 185, 129, 1)" },
            { bg: "rgba(245, 158, 11, 0.7)", border: "rgba(245, 158, 11, 1)" },
            { bg: "rgba(239, 68, 68, 0.7)", border: "rgba(239, 68, 68, 1)" },
            { bg: "rgba(168, 85, 247, 0.7)", border: "rgba(168, 85, 247, 1)" },
        ]

        const datasets = years
            .map((year, index) => {
                const yearMap = yearDataMap.get(year)
                if (!yearMap || year == null) return null
                const color = colors[index % colors.length]

                return {
                    label: String(year),
                    data: allMonths.map((month) => yearMap.get(month) || 0),
                    backgroundColor: color.bg,
                    borderColor: color.border,
                    borderWidth: 2,
                    borderRadius: 6,
                }
            })
            .filter((dataset): dataset is NonNullable<typeof dataset> => dataset !== null)

        return {
            labels: allMonths.map((m) => (m >= 1 && m <= 12 ? monthNames[m - 1] : "")),
            datasets,
        }
    }, [salesData, selectedSeller])

    // Calculate totals for selected seller
    const sellerTotals = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return {}

        const validData = salesData.filter(
            (d) =>
                d.vendedorNombre === selectedSeller &&
                d.year != null &&
                d.amount != null
        )

        const years = Array.from(new Set(validData.map((d) => d.year))).filter((y) => y != null)
        const totalsByYear: Record<number, number> = {}

        years.forEach((year) => {
            totalsByYear[year] = validData
                .filter((d) => d.year === year)
                .reduce((sum, d) => sum + (d.amount || 0), 0)
        })

        return totalsByYear
    }, [salesData, selectedSeller])

    const sellerGrandTotal = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return 0

        return salesData
            .filter(
                (d) =>
                    d.vendedorNombre === selectedSeller &&
                    d.amount != null
            )
            .reduce((sum, d) => sum + (d.amount || 0), 0)
    }, [salesData, selectedSeller])

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    font: {
                        size: 14,
                        weight: "bold" as const,
                    },
                    padding: 20,
                },
            },
            title: {
                display: true,
                text: `${t("Sales")} - ${selectedSeller} (${t("Year Comparison")})`,
                font: {
                    size: 20,
                    weight: "bold" as const,
                },
                padding: {
                    bottom: 30,
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context: { dataset: { label?: string }, parsed: { y: number | null } }) {
                        let label = context.dataset.label || ""
                        if (label) {
                            label += ": "
                        }
                        if (context.parsed.y !== null) {
                            label += FormatMoney(context.parsed.y, 2)
                        }
                        return label
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 12,
                        weight: "bold" as const,
                    },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(0, 0, 0, 0.05)",
                },
                ticks: {
                    font: {
                        size: 12,
                    },
                    callback: function (value: string | number) {
                        return FormatMoney(Number(value), 0)
                    },
                },
            },
        },
    }

    if (!user?.OrganizationId) {
        return (
            <Container fluid>
                <Card className="my-4">
                    <Card.Body>{t("Organization not selected")}</Card.Body>
                </Card>
            </Container>
        )
    }

    return (
        <Container fluid className="px-4">
            <Row className="my-4">
                <Col>
                    <h2 className="mb-0">
                        {t("Sales By Seller")} - {t("Year Comparison")}
                    </h2>
                </Col>
            </Row>

            {/* Seller Selector */}
            {sellers.length > 0 && (
                <Row className="mb-4">
                    <Col md={6} lg={4}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <label className="form-label fw-bold">{t("Select Seller")}</label>
                                <select
                                    className="form-select"
                                    value={selectedSeller || ""}
                                    onChange={(e) => setSelectedSeller(e.target.value || null)}
                                >
                                    {sellers.map((seller) => (
                                        <option key={seller} value={seller}>
                                            {seller}
                                        </option>
                                    ))}
                                </select>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Summary Cards */}
            {selectedSeller && (
                <Row className="mb-4">
                    {Object.entries(sellerTotals)
                        .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                        .map(([year, total]) => (
                            <Col key={year} md={6} lg={3} className="mb-3">
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Body>
                                        <div className="text-muted mb-1">
                                            {t("Total Sales")} {year}
                                        </div>
                                        <h3 className="mb-0">{FormatMoney(total, 2)}</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    <Col md={6} lg={3} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm bg-primary text-white">
                            <Card.Body>
                                <div className="mb-1 opacity-75">{t("Grand Total")}</div>
                                <h3 className="mb-0">{FormatMoney(sellerGrandTotal, 2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Chart */}
            <Row>
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <div className="mt-3">{t("Loading sales data")}...</div>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger" role="alert">
                                    {t("Error")}: {error}
                                </div>
                            ) : !chartData ? (
                                <div className="text-center py-5 text-muted">
                                    {t("No sales data available")}
                                </div>
                            ) : (
                                <div style={{ height: "500px" }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Data Table */}
            {!loading &&
                selectedSeller &&
                chartData &&
                salesData.filter(
                    (d) =>
                        d.vendedorNombre === selectedSeller &&
                        d.month >= 1 &&
                        d.month <= 12 &&
                        d.amount != null
                ).length > 0 && (
                    <Row className="mt-4">
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <h5 className="mb-3">{t("Detailed Data")}</h5>
                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t("Year")}</th>
                                                    <th>{t("Month")}</th>
                                                    <th className="text-end">{t("Amount")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {salesData
                                                    .filter(
                                                        (item) =>
                                                            item.vendedorNombre === selectedSeller &&
                                                            item.year != null &&
                                                            item.month != null &&
                                                            item.month >= 1 &&
                                                            item.month <= 12 &&
                                                            item.amount != null
                                                    )
                                                    .sort((a, b) => {
                                                        if (a.year !== b.year) return b.year - a.year
                                                        return a.month - b.month
                                                    })
                                                    .map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.year}</td>
                                                            <td>
                                                                {new Date(item.year, item.month - 1).toLocaleString(
                                                                    "default",
                                                                    { month: "long" }
                                                                )}
                                                            </td>
                                                            <td className="text-end">
                                                                {FormatMoney(item.amount || 0, 2)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
        </Container>
    )
}

export default SalesBySellerPage
